import {
  buildInplayV2SelectionIdentityKey,
  getCustomerMarketKey,
  getInplayV2SelectionStrictStableKey,
  getInplaySelectionStableKey,
} from '../../../_helpers/inplayMarketIdentity.js';

const getMarketSelectionsKey = (market = {}) => (
  Array.isArray(market.selections) && !Array.isArray(market.odds) ? 'selections' : 'odds'
);

const getMarketSelections = (market = {}) => (
  Array.isArray(market.odds) ? market.odds : Array.isArray(market.selections) ? market.selections : []
);

const getLineGroupSelectionsKey = (lineGroup = {}) => (
  Array.isArray(lineGroup.selections) && !Array.isArray(lineGroup.odds) ? 'selections' : 'odds'
);

const getLineGroupSelections = (lineGroup = {}) => (
  Array.isArray(lineGroup.odds)
    ? lineGroup.odds
    : Array.isArray(lineGroup.selections)
      ? lineGroup.selections
      : []
);

const firstPresent = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    return value;
  }
  return null;
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getInplayMarketsPatchOddId = (patch = {}) => {
  const value = firstPresent(patch.odds_id, patch.oddsId, patch.id);
  if (value === null) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : value;
};

export const getInplayMarketsPatchStableKey = (patch = {}) => {
  const target = patch.target || patch.v2_identity || {};
  return buildInplayV2SelectionIdentityKey({
    fixtureId: firstPresent(target.fixtureId, target.fixture_id, patch.fixtureId, patch.fixture_id),
    marketId: firstPresent(target.marketId, target.market_id, patch.marketId, patch.market_id),
    productMarketId: firstPresent(
      target.productMarketId,
      target.product_market_id,
      patch.productMarketId,
      patch.product_market_id
    ),
    lineId: firstPresent(target.lineId, target.line_id, patch.lineId, patch.line_id),
    selectionId: firstPresent(target.selectionId, target.selection_id, patch.selectionId, patch.selection_id),
  });
};

export const hasInplayMarketsPatchV2Identity = (patch = {}) => {
  const target = patch.target || patch.v2_identity || {};
  return [
    target.fixtureId,
    target.fixture_id,
    patch.fixtureId,
    patch.fixture_id,
    target.marketId,
    target.market_id,
    patch.marketId,
    patch.market_id,
    target.productMarketId,
    target.product_market_id,
    patch.productMarketId,
    patch.product_market_id,
    target.lineId,
    target.line_id,
    patch.lineId,
    patch.line_id,
    target.selectionId,
    target.selection_id,
    patch.selectionId,
    patch.selection_id,
  ].some((value) => firstPresent(value) !== null);
};

export const buildInplayMarketsPatchSelection = (patch = {}, payload = {}) => {
  const oddsId = getInplayMarketsPatchOddId(patch);
  const target = patch.target || patch.v2_identity || {};
  const odds = toFiniteNumber(firstPresent(patch.displayPrice, patch.display_price, patch.odds, patch.providerPrice, patch.provider_price));
  return {
    ...(oddsId !== null ? { id: oddsId, odds_id: oddsId } : {}),
    ...(odds !== null ? { odds } : {}),
    ...(patch.is_suspended !== undefined ? { is_suspended: patch.is_suspended } : {}),
    ...(patch.materialHash ? { materialHash: patch.materialHash } : {}),
    v2_identity: {
      fixtureId: firstPresent(target.fixtureId, target.fixture_id, patch.fixtureId, patch.fixture_id),
      marketId: firstPresent(target.marketId, target.market_id, patch.marketId, patch.market_id),
      productMarketId: firstPresent(
        target.productMarketId,
        target.product_market_id,
        patch.productMarketId,
        patch.product_market_id
      ),
      lineSetId: firstPresent(target.lineSetId, target.line_set_id, patch.lineSetId, patch.line_set_id),
      lineId: firstPresent(target.lineId, target.line_id, patch.lineId, patch.line_id),
      selectionId: firstPresent(target.selectionId, target.selection_id, patch.selectionId, patch.selection_id),
    },
    ...(firstPresent(patch.authority_ts, payload.authority_ts) ? {
      authority_ts: firstPresent(patch.authority_ts, payload.authority_ts),
    } : {}),
    ...(firstPresent(patch.sequence, payload.sequence) !== null ? {
      sequence: firstPresent(patch.sequence, payload.sequence),
    } : {}),
  };
};

const shouldPatchSelection = (selection = {}, market = {}, patch = {}) => {
  if (hasInplayMarketsPatchV2Identity(patch)) {
    const patchKey = getInplayMarketsPatchStableKey(patch);
    if (!patchKey) return false;
    return getInplayV2SelectionStrictStableKey(selection, market) === patchKey;
  }

  const patchOddId = getInplayMarketsPatchOddId(patch);
  const selectionOddId = firstPresent(selection.id, selection.odds_id, selection.oddsId);
  if (patchOddId !== null && selectionOddId !== null && String(patchOddId) === String(selectionOddId)) return true;

  const patchKey = getInplayMarketsPatchStableKey(patch);
  if (!patchKey) return false;
  return getInplaySelectionStableKey(selection, market) === patchKey;
};

const patchSelection = (selection = {}, market = {}, patches = [], payload = {}) => {
  const patch = patches.find((candidate) => shouldPatchSelection(selection, market, candidate));
  if (!patch) return { selection, changed: false };
  const patchSelectionRow = buildInplayMarketsPatchSelection(patch, payload);
  return {
    selection: {
      ...selection,
      ...patchSelectionRow,
      v2_identity: {
        ...(selection.v2_identity || {}),
        ...(patchSelectionRow.v2_identity || {}),
      },
    },
    changed: true,
  };
};

const patchSelectionArray = (selections = [], market = {}, patches = [], payload = {}) => {
  let changed = false;
  const nextSelections = selections.map((selection) => {
    const result = patchSelection(selection, market, patches, payload);
    if (result.changed) changed = true;
    return result.selection;
  });
  return { selections: nextSelections, changed };
};

const patchLineGroup = (lineGroup = {}, market = {}, patches = [], payload = {}) => {
  const selections = getLineGroupSelections(lineGroup);
  if (!selections.length) return { lineGroup, changed: false };
  const result = patchSelectionArray(selections, market, patches, payload);
  if (!result.changed) return { lineGroup, changed: false };
  const selectionKey = getLineGroupSelectionsKey(lineGroup);
  const withoutSelectionKeys = selectionKey === 'selections'
    ? (({ odds: _odds, ...rest }) => rest)(lineGroup)
    : (({ selections: _selections, ...rest }) => rest)(lineGroup);
  return {
    lineGroup: {
      ...withoutSelectionKeys,
      [selectionKey]: result.selections,
    },
    changed: true,
  };
};

export const applyInplayMarketsPatchToMarketTree = (markets = [], patches = [], payload = {}) => {
  if (!Array.isArray(markets) || !Array.isArray(patches) || patches.length === 0) {
    return { markets, appliedCount: 0 };
  }

  let appliedCount = 0;
  const nextMarkets = markets.map((market) => {
    let nextMarket = market;
    let marketChanged = false;

    const marketSelections = getMarketSelections(market);
    if (marketSelections.length) {
      const selectionResult = patchSelectionArray(marketSelections, market, patches, payload);
      if (selectionResult.changed) {
        const selectionKey = getMarketSelectionsKey(market);
        const withoutSelectionKeys = selectionKey === 'selections'
          ? (({ odds: _odds, ...rest }) => rest)(nextMarket)
          : (({ selections: _selections, ...rest }) => rest)(nextMarket);
        nextMarket = {
          ...withoutSelectionKeys,
          [selectionKey]: selectionResult.selections,
        };
        marketChanged = true;
      }
    }

    if (Array.isArray(market.lineGroups)) {
      const nextLineGroups = market.lineGroups.map((lineGroup) => {
        const result = patchLineGroup(lineGroup, market, patches, payload);
        if (result.changed) marketChanged = true;
        return result.lineGroup;
      });
      if (marketChanged) nextMarket = { ...nextMarket, lineGroups: nextLineGroups };
    }

    if (marketChanged) appliedCount += 1;
    return nextMarket;
  });

  return {
    markets: appliedCount > 0 ? nextMarkets : markets,
    appliedCount,
  };
};

export const collectInplayMarketTreeRefs = (markets = []) => {
  const oddsIds = new Set();
  const marketKeys = new Set();
  const visitMarket = (market = {}) => {
    const marketKey = getCustomerMarketKey(market);
    if (marketKey != null) marketKeys.add(String(marketKey));

    getMarketSelections(market).forEach((selection) => {
      const id = firstPresent(selection.id, selection.odds_id, selection.oddsId);
      if (id !== null) oddsIds.add(String(id));
      const selectionMarketKey = getCustomerMarketKey(selection, market);
      if (selectionMarketKey != null) marketKeys.add(String(selectionMarketKey));
    });

    (market.lineGroups || []).forEach((lineGroup) => {
      getLineGroupSelections(lineGroup).forEach((selection) => {
        const id = firstPresent(selection.id, selection.odds_id, selection.oddsId);
        if (id !== null) oddsIds.add(String(id));
        const selectionMarketKey = getCustomerMarketKey(selection, market);
        if (selectionMarketKey != null) marketKeys.add(String(selectionMarketKey));
      });
    });
  };

  if (Array.isArray(markets)) markets.forEach(visitMarket);
  return { oddsIds, marketKeys };
};
